/**
 * スペースキーによる「最後にアクティブなオーディオ」のグローバル再生制御。
 *
 * 再生開始・シーク・選択などで最後にアクティブになったオーディオトランスポートを
 * モジュールレベルで 1 つだけ記録し、入力欄やボタン以外にフォーカスがある状態で
 * スペースキーが押されたら、そのオーディオの再生/一時停止をトグルする。
 * Messenger のように専用のキーボードショートカットを持たない画面でも、
 * 「直前に触った音源」をスペースキーで再生できるようにする。
 *
 * Preview ページのように独自のスペースキーショートカットを持つ画面も、
 * 現在スライドのメディアを setSpacePlaybackTarget で登録し、自前のハンドラから
 * toggleSpacePlaybackTarget へ委譲することでこの仕組みに参加する。これにより
 * コメントパネル内の添付オーディオを触った直後はそちらが優先され、スライド切替や
 * スライドメディアの再生開始で再びスライド側へ優先が戻る。
 *
 * 衝突回避:
 * - React コンポーネント側で処理済み（preventDefault 済み）のイベントには反応しない
 *   （試聴リストのフォーカス行トグル、RadioCardGroup の選択確定、Preview ページの
 *   ショートカットなど。React はルートコンテナへ委譲するため、window バブルより
 *   先に評価される）
 * - 入力系（input / textarea / contentEditable）とインタラクティブ要素
 *   （button / a / select 等）にフォーカスがあるときは奪わない
 */

type ToggleFn = () => void;

let currentTarget: { key: symbol; toggle: ToggleFn } | null = null;
let listenerInstalled = false;

/** 入力・操作系要素上のスペースキーは本来の動作（文字入力・ボタン押下）を優先する */
function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return !!target.closest(
    'input, textarea, select, button, a, [role="button"], [contenteditable="true"]',
  );
}

function handleKeyDown(e: KeyboardEvent): void {
  if (e.key !== ' ' || e.repeat || e.isComposing) return;
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
  // ページ・コンポーネント固有のスペースキー処理が済んでいるなら譲る
  if (e.defaultPrevented) return;
  if (!currentTarget) return;
  if (isInteractiveTarget(e.target)) return;
  e.preventDefault();
  currentTarget.toggle();
}

function ensureListener(): void {
  if (listenerInstalled || typeof window === 'undefined') return;
  listenerInstalled = true;
  window.addEventListener('keydown', handleKeyDown);
}

/**
 * スペースキー再生のターゲットを設定する（最後に呼んだものが勝つ）。
 * useAudioTransport が再生開始・シーク・明示的なアクティブ化時に呼ぶほか、
 * Preview ページが現在スライドのメディアを登録するのにも使う。
 */
export function setSpacePlaybackTarget(key: symbol, toggle: ToggleFn): void {
  currentTarget = { key, toggle };
  ensureListener();
}

/** 自分がターゲットの場合のみ解除する（トランスポート破棄時） */
export function clearSpacePlaybackTarget(key: symbol): void {
  if (currentTarget?.key === key) {
    currentTarget = null;
  }
}

/**
 * 現在のターゲットの再生/一時停止をトグルする。
 * 独自のスペースキーショートカットを持つページ（Preview 等）が、自前のハンドラから
 * 「最後にアクティブなオーディオ」へ委譲するために呼ぶ。
 * @returns ターゲットが存在して処理した場合 true（呼び出し側のフォールバック判定用）
 */
export function toggleSpacePlaybackTarget(): boolean {
  if (!currentTarget) return false;
  currentTarget.toggle();
  return true;
}
